import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from 'react-query';

import { EmojiNames } from '@common/types/api';
import {
  useBroadcastQuery,
  useEmojiMutation,
  useEmojiQuery,
  useEventfy,
} from '@hooks';
import { debounce } from '@utils';
import { selectEmojiSettings } from '@hooks/useEventfy/selectors';

import * as S from './styles';

const Emoji = () => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const floatingAreaRef = useRef<HTMLDivElement>(null);

  const emojiSettings = useEventfy(selectEmojiSettings);

  const { data: settings } = useBroadcastQuery({
    select: (room) => ({
      roomId: room.id,
      isEmojiEnabled: !!room.settings.data.emoji,
    }),
    notifyOnChangeProps: ['data'],
  });

  const { data: emojiData } = useEmojiQuery(settings?.roomId);

  const { mutateAsync } = useEmojiMutation(settings?.roomId);

  const handleEmojiInteraction = useRef(
    debounce(async (emojiName: EmojiNames) => {
      await mutateAsync(emojiName);
    }, 1500),
  ).current;

  const emojis = useMemo(
    () =>
      Object.entries(emojiSettings).filter(
        ([name]) => !['id', 'setting_id'].includes(name),
      ) as unknown as Array<[EmojiNames, string]>,
    [emojiSettings],
  );

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const spawnFloatingEmoji = useCallback((src: string) => {
    const area = floatingAreaRef.current;
    if (!area) return;

    const img = document.createElement('img');
    img.src = src;
    img.style.position = 'absolute';
    img.style.bottom = '0';
    img.style.width = '36px';
    img.style.height = '36px';
    img.style.pointerEvents = 'none';
    img.style.left = `${Math.random() * 68}px`;

    area.appendChild(img);

    requestAnimationFrame(() => {
      img.style.transition = 'all 2.5s ease-out';
      img.style.transform = 'translateY(-120px) scale(0.8)';
      img.style.opacity = '0';
    });

    setTimeout(() => {
      img.remove();
    }, 2500);
  }, []);

  useEffect(() => {
    if (settings?.isEmojiEnabled) {
      queryClient.invalidateQueries(['emoji', settings?.roomId]);
    }
  }, [settings?.isEmojiEnabled, settings?.roomId, queryClient]);

  return (
    <S.Container $disabled={!settings?.isEmojiEnabled}>
      <S.FloatingReactionsArea ref={floatingAreaRef} />

      <S.ExpandPanel $isOpen={isOpen}>
        {emojis.map(([emoji, src], index) => (
          <S.EmojiItem
            key={emoji}
            $index={index}
            $total={emojis.length}
            $isOpen={isOpen}
          >
            <S.EmojiCircle
              type="button"
              onClick={() => {
                handleEmojiInteraction(emoji);
                spawnFloatingEmoji(src);
              }}
            >
              <img src={src} alt={emoji} />
              <S.EmojiCount $hot={Number(emojiData?.[emoji] || 0) >= 15}>
                {emojiData?.[emoji] || 0}
              </S.EmojiCount>
            </S.EmojiCircle>
          </S.EmojiItem>
        ))}
      </S.ExpandPanel>

      <S.TriggerButton onClick={togglePanel} $isOpen={isOpen} type="button">
        <span>😊</span>
        <S.PlusBadge $isOpen={isOpen}>+</S.PlusBadge>
      </S.TriggerButton>
    </S.Container>
  );
};

export default Emoji;
